import React, { useId } from 'react';
import { Controller } from 'react-hook-form';

import {
	Button,
	LegacyGrid as Grid,
	LegacyGridCol as GridCol,
	Input,
	Popup,
	PopupSection,
	Select,
	SelectOption,
	Text,
} from '@flixbus/honeycomb-react';

import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';

import { MANAGE_EXTRA_ACTIVITY_MODES } from './const';
import { MANAGE_EXTRA_ACTIVITY_TEXT } from './text';
import { useManageExtraActivity } from './useManageExtraActivity';

import styles from './styles.module.css';

const ManageExtraActivity = ({ mode = MANAGE_EXTRA_ACTIVITY_MODES.add, ...props }) => {
	const locationId = useId();
	const activityId = useId();
	const durationId = useId();

	const {
		control,
		loading,
		isSubmitDisabled,
		locations,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		cannotCancelOverlaps,
		handleClose,
		handleSubmit,
		availableActivityOptions,
		isActivityFieldsDisabled,
		resetOverlapsOnChange,
		setOverlappingAction,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		validateActivityType,
	} = useManageExtraActivity({ mode, ...props });

	const isAddMode = mode === MANAGE_EXTRA_ACTIVITY_MODES.add;

	return (
		<Popup
			id="manage-extra-activity"
			active
			onOverlayClick={handleClose}
			extraClasses={styles.modalWrapper}
		>
			<PopupSection type="title">
				{isAddMode ? MANAGE_EXTRA_ACTIVITY_TEXT.addTitle : MANAGE_EXTRA_ACTIVITY_TEXT.manageTitle}
			</PopupSection>

			<PopupSection type="content" extraClasses={styles.content}>
				<Text extraClasses={styles.description}>
					{isAddMode
						? MANAGE_EXTRA_ACTIVITY_TEXT.description
						: MANAGE_EXTRA_ACTIVITY_TEXT.manageDescription}
				</Text>

				<form
					onSubmit={(event) => {
						event.preventDefault();
						handleSubmit();
					}}
				>
					<Grid gutterSize="4">
						<GridCol size={12} extraClasses={styles.gridItem}>
							<Controller
								name="stationId"
								control={control}
								render={({ field: { ref: _ref, ...field } }) => (
									<Select
										{...field}
										id={locationId}
										label={MANAGE_EXTRA_ACTIVITY_TEXT.locationLabel}
										placeholder={MANAGE_EXTRA_ACTIVITY_TEXT.locationPlaceholder}
										disabled={isActivityFieldsDisabled}
										onChange={(event) => {
											field.onChange(event.target.value);
											resetOverlapsOnChange();
										}}
									>
										{locations.map((station) => (
											<SelectOption key={station.id} value={station.id}>
												{station.name}
											</SelectOption>
										))}
									</Select>
								)}
							/>
						</GridCol>

						<GridCol size={12} extraClasses={styles.gridItem}>
							<Controller
								name="activityType"
								control={control}
								rules={{ validate: validateActivityType }}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Select
										{...field}
										id={activityId}
										label={MANAGE_EXTRA_ACTIVITY_TEXT.extraActivityLabel}
										placeholder={MANAGE_EXTRA_ACTIVITY_TEXT.extraActivityPlaceholder}
										disabled={isActivityFieldsDisabled}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										onChange={(event) => {
											field.onChange(event.target.value);
											resetOverlapsOnChange();
										}}
									>
										{availableActivityOptions.map(({ type, label }) => (
											<SelectOption key={type} value={type}>
												{label}
											</SelectOption>
										))}
									</Select>
								)}
							/>
						</GridCol>

						<GridCol size={5} extraClasses={styles.gridItem}>
							<Controller
								name="duration"
								control={control}
								render={({ field: { ref: _ref, ...field } }) => (
									<Input
										{...field}
										id={durationId}
										type="time"
										label={MANAGE_EXTRA_ACTIVITY_TEXT.durationLabel}
										onChange={(event) => {
											field.onChange(event.target.value);
											resetOverlapsOnChange();
										}}
									/>
								)}
							/>
						</GridCol>
					</Grid>

					{hasOverlappingActivities && (
						<OverlappingBanner
							overlappingList={overlappingActivities}
							overlappingAction={overlappingAction}
							onOverlappingActionChange={setOverlappingAction}
							cannotCancelOverlaps={cannotCancelOverlaps}
							isDelayOptionAvailable={isDelayOptionAvailable}
							delayMinutes={delayMinutes}
							delayError={delayError}
							minDelayMinutes={minDelayMinutes}
							onDelayMinutesChange={setDelayMinutes}
						/>
					)}
				</form>
			</PopupSection>

			<PopupSection type="actions" extraClasses={styles.actions}>
				<Button type="button" onClick={handleClose}>
					{MANAGE_EXTRA_ACTIVITY_TEXT.cancelButton}
				</Button>

				<Button
					type="submit"
					appearance="primary"
					disabled={isSubmitDisabled}
					loading={loading}
					onClick={handleSubmit}
				>
					{isAddMode ? MANAGE_EXTRA_ACTIVITY_TEXT.addButton : MANAGE_EXTRA_ACTIVITY_TEXT.saveButton}
				</Button>
			</PopupSection>
		</Popup>
	);
};

export default ManageExtraActivity;
